import { GetServerSideProps, NextPage } from "next";
import { useEffect, useMemo } from "react";
import { FamProfileDetails } from "../../components/profile/components/details";
import FamProfileGallery from "../../components/profile/profileGallery";
import {
  ProfileAnswer,
  PublicAdoptiveParentProfile,
} from "../../types/coreApiMirror";
import { PageProps } from "../../types/pageProps";
import { shuffle } from "../../utils/shuffle";
import { CoreAPI } from "../../utils/coreClient";
import Block from "../../components/block";
import ProfilePlaceholder from "../../components/profilePlaceholder";
import { getClassName } from "@pairtreefamily/utils";
import GalleryTile from "../../components/profile/galleryTile";
import { useAnalytics } from "../../utils/useAnalytics";
import { useHasMounted } from "../../hooks/useHasMounted";
import { useCoreAPI } from "../../hooks/useCoreAPI";
import { useAccount } from "../../hooks/useAccount";
import { LinkTo } from "../../components/linkTo";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import { getClient } from "lib/sanity.server";
import Image from "next/image";
import { Breadcrumbs } from "@/components/breadcrumbs";
import SEOPageHead from "@/components/SEOPageHead";
import { trackEvent } from "@/components/analytics";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";
import FooterModular from "@/components/FooterModular";

type FamilyProfileProps = PageProps<
  PublicAdoptiveParentProfile &
    HeaderNavigationProps & { answers: ProfileAnswer[] } & {
      footerPayload: FooterNavigationProps;
    }
>;

const token = process.env.SANITY_API_TOKEN;

const FamilyProfile: NextPage<FamilyProfileProps> = ({ payload }) => {
  const {
    adoptive_parents,
    welcome_message,
    photos,
    banner_photo,
    answers,
    navSections,
    slug,
    footerPayload,
  } = payload.data;

  const imageUrl = banner_photo?.media_url;

  const { sendEvent } = useAnalytics();

  const hasMounted = useHasMounted();
  const coreAPI = useCoreAPI();
  const { account } = useAccount();

  useEffect(() => {
    if (hasMounted) {
      sendEvent("view_profile", {});
      trackEvent("view_family_profile", { family_profile: slug });

      if (account) {
        coreAPI.addProfileView(payload.data.uuid);
      } else {
        coreAPI.addAnonymousProfileView(payload.data.uuid);
      }
    }
  }, [hasMounted, sendEvent, account, coreAPI, payload.data.uuid]);

  const sortedMedia = useMemo(() => {
    return photos.sort((a, b) => {
      if (a.sort_order < b.sort_order) {
        return -1;
      } else if (a.sort_order > b.sort_order) {
        return 1;
      }
      return 0;
    });
  }, [photos]);

  const profileImageClass = getClassName("h-full object-cover w-full");

  function getAdoptiveParentNames(
    parents: PublicAdoptiveParentProfile["adoptive_parents"],
  ) {
    const names = parents.map((p, i) => {
      return p.first_name;
    });
    return names.join(" & ");
  }

  const adoptiveParentNames = getAdoptiveParentNames(
    payload.data.adoptive_parents,
  );

  const breadcrumbs = [
    { name: "Find a Family", path: "/waiting-families" },
    {
      name: adoptiveParentNames,
      path: `/waiting-families/${payload.data.slug}`,
    },
  ];

  const seo = { title: `${adoptiveParentNames} • Waiting Families` };

  return (
    <>
      {seo && <SEOPageHead seo={seo} />}
      <HeaderNavigation navSections={navSections} />
      <Block flushBottom flushTop className="mt-20 pt-5" color="backgroundgray">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </Block>
      <Block color="backgroundgray" flushTop>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FamProfileDetails {...payload.data} />

          <GalleryTile
            size="lg"
            className="relative min-h-[340px] p-0 md:min-h-[480px]"
          >
            {imageUrl ? (
              <Image
                className={profileImageClass}
                alt="Photograph of adoptive Family"
                src={imageUrl}
                fill
                sizes="max-width: 1024px, 100vw, 50vw"
                loading="lazy"
              />
            ) : (
              <ProfilePlaceholder className={profileImageClass} />
            )}
          </GalleryTile>

          <FamProfileGallery
            state={payload.data.state}
            homeStudyApproved={payload.data.home_study_verified ?? false}
            parents={adoptive_parents}
            welcomeMessage={welcome_message}
            media={sortedMedia}
            answers={answers}
            video_url={payload.data.video_url}
            video_thumb={payload.data.banner_photo}
          />
        </div>
        {/* An additional CTA to start a conversation. Adds onto the existing one in FamProfileDetails. */}
        <div className="my-6 w-full">
          <LinkTo.PairTreeApp
            style="button"
            buttonColor="green"
            buttonStyle="primary"
            className="text-body-3-medium"
          >
            <b>Start a Conversation</b>
          </LinkTo.PairTreeApp>
        </div>
      </Block>
      <FooterModular footerData={footerPayload} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  FamilyProfileProps
> = async (context) => {
  const preview: boolean = !!context.draftMode;
  const slug = context.params?.slug;

  if (!slug || Array.isArray(slug)) {
    return {
      notFound: true,
    };
  }
  const result = await new CoreAPI().getProfileDetails(slug);

  if (!result.ok) {
    if (result.error.type === "profileNotFound") {
      return {
        notFound: true,
      };
    }
    // throw result.error; not sure if we want to throw an error here
    return {
      notFound: true,
    };
  }

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerPayload: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  // shuffle the answers on the server so we have consistent client ordering
  // for hydration.
  const profileContent: PublicAdoptiveParentProfile &
    HeaderNavigationProps & { footerPayload: FooterNavigationProps } & {
      answers: ProfileAnswer[];
    } = {
    ...navPayload,
    footerPayload: footerPayload,
    ...result.content,
    answers: shuffle(
      result.content.adoptive_parents
        .reduce((acc: ProfileAnswer[], cur) => acc.concat(cur.answers), [])
        .filter((a) => a.question.name !== "" && a.answer !== ""),
    ),
  };

  return {
    props: {
      payload: {
        data: profileContent,
        query: "",
        queryParams: {},
      },
      preview,
      token: preview ? token : "",
    },
  };
};

export default FamilyProfile;
